import type { OnboardingChainStepState } from '@/types/onboarding-chain'

/** The pace the arrival plays at: its beats are designed at full speed and play at three quarters of it. */
export const ARRIVAL_PACE = 0.75

/** The easing tokens the arrival runs on, named after their `--ease-*` token. */
export type ArrivalEasing = 'soft' | 'soft-exit' | 'standard'

/** When and how one part of the arrival plays, in milliseconds from the moment the shell mounts. */
export interface ArrivalBeat {
  readonly delay: number
  readonly duration: number
  readonly easing: ArrivalEasing
}

/** How the bare screen leaves: the screen blurs away, then the ground it sat on fades to the shell. */
export interface ArrivalExit {
  /** The bare screen itself leaving. */
  readonly screen: ArrivalBeat
  /** The bare ground fading out, which uncovers the shell beneath it. */
  readonly ground: ArrivalBeat
}

/** The one flare of the current step's lamp: which step row it lights, and when. */
interface ArrivalFlare {
  readonly step: number
  readonly beat: ArrivalBeat
}

/** Every beat of the shell's arrival, in the order the founder sees them. */
export interface ArrivalSchedule {
  /** The mark blur-fades in at the centre of the shell. */
  readonly markIn: ArrivalBeat
  /** The mark moves to its side-nav place and shrinks to its nav size. */
  readonly markDock: ArrivalBeat
  /** The page tile slides in from beyond the right edge. */
  readonly tile: ArrivalBeat
  /** The wordmark blur-fades in beside the docked mark. */
  readonly wordmark: ArrivalBeat
  /** One beat per step row, top to bottom. */
  readonly steps: readonly ArrivalBeat[]
  /** One beat per rail between two step rows, drawn down from the row above. */
  readonly rails: readonly ArrivalBeat[]
  /** One beat per part of the page content, in reading order. */
  readonly content: readonly ArrivalBeat[]
  /** The account row at the foot of the side nav. */
  readonly footer: ArrivalBeat
  /** One beat per step row: the lamp lighting up, or `null` for a step still to do. */
  readonly lamps: readonly (ArrivalBeat | null)[]
  /** The flare of the current step's lamp, or `null` when no step is current. */
  readonly flare: ArrivalFlare | null
  /** When the last beat ends. */
  readonly end: number
}

/** What the shell shows when it arrives: the state of each step row and how many content parts the page marks. */
export interface ArrivalCast {
  readonly stepStates: readonly OnboardingChainStepState[]
  readonly contentParts: number
}

const DESIGNED_MS = {
  exitScreen: 420,
  exitGroundDelay: 120,
  exitGround: 530,
  markIn: 1000,
  markDockDelay: 1400,
  markDock: 950,
  tileDelay: 1800,
  tile: 1000,
  wordmarkDelay: 2150,
  wordmark: 560,
  stepsDelay: 2500,
  stepStagger: 120,
  step: 600,
  railLag: 240,
  rail: 360,
  contentLag: 60,
  content: 680,
  footerLag: 300,
  footer: 600,
  lampLag: 480,
  lamp: 360,
  flareLag: 80,
  flare: 720,
} as const

/** How long the bare screen fades out under reduced motion; matches `--transition-duration-base`. */
export const ARRIVAL_REDUCED_EXIT_MS = 200

function paced(designed: number): number {
  return Math.round(designed / ARRIVAL_PACE)
}

function beat(designedDelay: number, designedDuration: number, easing: ArrivalEasing): ArrivalBeat {
  return { delay: paced(designedDelay), duration: paced(designedDuration), easing }
}

function endOf(entry: ArrivalBeat): number {
  return entry.delay + entry.duration
}

/** The bare screen's exit, which runs over the shell while the shell's own beats are still waiting. */
export const ARRIVAL_EXIT: ArrivalExit = {
  screen: beat(0, DESIGNED_MS.exitScreen, 'soft-exit'),
  ground: beat(DESIGNED_MS.exitGroundDelay, DESIGNED_MS.exitGround, 'standard'),
}

const REDUCED_FADE: ArrivalBeat = {
  delay: 0,
  duration: ARRIVAL_REDUCED_EXIT_MS,
  easing: 'standard',
}

/** The same exit under reduced motion: the screen and its ground fade together, nothing blurs or moves. */
export const ARRIVAL_REDUCED_EXIT: ArrivalExit = { screen: REDUCED_FADE, ground: REDUCED_FADE }

/** Times every beat of the shell's arrival: beats start once the bare screen's exit is over, lit lamps light in order after the last row lands. */
export function arrivalSchedule(cast: ArrivalCast): ArrivalSchedule {
  const start = DESIGNED_MS.exitGroundDelay + DESIGNED_MS.exitGround
  const stepsStart = start + DESIGNED_MS.stepsDelay
  const stepStart = (index: number): number => stepsStart + index * DESIGNED_MS.stepStagger

  const steps = cast.stepStates.map((_, index) => beat(stepStart(index), DESIGNED_MS.step, 'soft'))

  const rails = cast.stepStates
    .slice(0, -1)
    .map((_, index) => beat(stepStart(index) + DESIGNED_MS.railLag, DESIGNED_MS.rail, 'standard'))

  const content = Array.from({ length: cast.contentParts }, (_, index) =>
    beat(stepStart(index) + DESIGNED_MS.contentLag, DESIGNED_MS.content, 'soft'),
  )

  const lampsStart = stepStart(Math.max(cast.stepStates.length - 1, 0)) + DESIGNED_MS.lampLag
  const litSteps = cast.stepStates.flatMap((state, index) => (state === 'todo' ? [] : [index]))
  const lampStart = (order: number): number => lampsStart + order * DESIGNED_MS.stepStagger

  const lamps = cast.stepStates.map((_, index) => {
    const order = litSteps.indexOf(index)
    return order === -1 ? null : beat(lampStart(order), DESIGNED_MS.lamp, 'standard')
  })

  const currentStep = cast.stepStates.indexOf('now')
  const currentOrder = litSteps.indexOf(currentStep)
  const flare: ArrivalFlare | null =
    currentOrder === -1
      ? null
      : {
          step: currentStep,
          beat: beat(lampStart(currentOrder) + DESIGNED_MS.flareLag, DESIGNED_MS.flare, 'soft'),
        }

  const markIn = beat(start, DESIGNED_MS.markIn, 'soft')
  const markDock = beat(start + DESIGNED_MS.markDockDelay, DESIGNED_MS.markDock, 'standard')
  const tile = beat(start + DESIGNED_MS.tileDelay, DESIGNED_MS.tile, 'soft')
  const wordmark = beat(start + DESIGNED_MS.wordmarkDelay, DESIGNED_MS.wordmark, 'soft')
  const footer = beat(stepsStart + DESIGNED_MS.footerLag, DESIGNED_MS.footer, 'soft')

  const every = [
    markIn,
    markDock,
    tile,
    wordmark,
    footer,
    ...steps,
    ...rails,
    ...content,
    ...lamps.filter((entry): entry is ArrivalBeat => entry !== null),
    ...(flare === null ? [] : [flare.beat]),
  ]

  return {
    markIn,
    markDock,
    tile,
    wordmark,
    steps,
    rails,
    content,
    footer,
    lamps,
    flare,
    end: Math.max(...every.map(endOf)),
  }
}
